import {
    GlobalNavNestedSidebar,
    GlobalNavNestedSidebarItem,
} from "panels/global-nav/GlobalNavNestedSidebar"
import * as Icons from "@nova/component-icons"
import { createContext, FC, PropsWithChildren, useContext, useLayoutEffect, useState } from "react"
import { BetaTag } from "features/BetaTag"
import { SyncAllAccountsFooter } from "./SyncAllAccountsFooter"

const PilotTrackingNestedSidebarContext = createContext<{
    Sidebar: FC
    register: (sidebar: FC) => void
    unregister: () => void
}>({
    Sidebar: DefaultPilotTrackingNestedSidebar,
    register: () => {},
    unregister: () => {},
})
export const usePilotTrackingNestedSidebarContext = () => {
    return useContext(PilotTrackingNestedSidebarContext)
}
export const PilotTrackingNestedSidebarContextProvider = ({ children }: PropsWithChildren) => {
    const [Sidebar, setSidebar] = useState<FC>(() => DefaultPilotTrackingNestedSidebar)
    return (
        <PilotTrackingNestedSidebarContext.Provider
            value={{
                Sidebar,
                register: (sidebar: FC) => setSidebar(() => sidebar),
                unregister: () => setSidebar(() => DefaultPilotTrackingNestedSidebar),
            }}
        >
            {children}
        </PilotTrackingNestedSidebarContext.Provider>
    )
}
export const useNonDefaultPilotTrackingNestedSidebar = ({ Sidebar }: { Sidebar: FC }) => {
    const { register, unregister } = usePilotTrackingNestedSidebarContext()
    useLayoutEffect(() => {
        register(Sidebar)
        return unregister
    }, [Sidebar, register, unregister])
}

export function DefaultPilotTrackingNestedSidebar() {
    return (
        <GlobalNavNestedSidebar
            title={
                <div className="flex gap-2">
                    Pilot Tracking
                    <BetaTag />
                </div>
            }
            footer={<SyncAllAccountsFooter />}
        >
            <div className="flex flex-col gap-px p-3">
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet" }}
                    Icon={Icons.BoundingBox2}
                    title="Dashboard"
                />
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet/flights" }}
                    Icon={Icons.ConversionPath}
                    title="Flights"
                />
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet/pilots" }}
                    Icon={Icons.PersonBook}
                    title="Pilots"
                    nested={[
                        {
                            to: { path: "/fleet/pilots" },
                            Icon: Icons.ContactPage,
                            title: "All Pilots",
                        },
                        {
                            to: { path: "/fleet/pilots/currency" },
                            Icon: Icons.BrowseGallery,
                            title: "Currency",
                        },
                    ]}
                />
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet/aircraft" }}
                    Icon={Icons.Drone2}
                    title="Drones"
                    nested={[
                        {
                            to: { path: "/fleet/aircraft" },
                            Icon: Icons.FormatListBulleted,
                            title: "All Drones",
                        },
                        {
                            to: { path: "/fleet/maintenance" },
                            Icon: Icons.Build,
                            title: "Maintenance",
                        },
                    ]}
                />
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet/checklists" }}
                    Icon={Icons.Preliminary}
                    title="Checklists"
                />
                <GlobalNavNestedSidebarItem
                    to={{ path: "/fleet/reports" }}
                    Icon={Icons.Docs}
                    title="Reports"
                />
            </div>
        </GlobalNavNestedSidebar>
    )
}
